import { GenericTable } from '@components/GenericTable';
import { useCampaignsByWorkflow } from '@hooks/useCampaigns';
import { useCursorPagination } from '@hooks/useCursorPagination';
import { useOrgAndPrograms } from '@hooks/useOrgAndPrograms';
import { useUsersByIds } from '@hooks/useUsers';
import { useWorkflowsByProgram } from '@hooks/useWorkflows';
import { useStore } from '@store/index';
import { Upload } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ErrorContainer } from '@/components/ErrorContainer';
import { Breadcrumb, BreadcrumbPortal, HeaderActionsPortal } from '@/components/Header';
import { createCampaignsTableConfig } from '@/components/TableBuilder';
import { Button } from '@/components/ui/button';
import { PAGE_SIZE_OPTIONS } from '@/constants/pagination';
import { getErrorButtonConfig } from '@/utils/errorButtonConfig';

import { BulkCreateCasesModal } from './CreateCampaign';

const CampaignsContainer: React.FC = () => {
  const { selectedProgramId, isLoadingOrgAndPrograms } = useOrgAndPrograms();
  const { selectedWorkflowId, campaignPageSize, setCampaignPageSize } = useStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Keep URL in sync when workflow changes (e.g., via header combobox)
  useEffect(() => {
    const urlWorkflowId = searchParams.get('workflow_id');
    if (selectedWorkflowId && selectedWorkflowId !== urlWorkflowId) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('workflow_id', selectedWorkflowId);
      navigate(`?${newParams.toString()}`, { replace: true });
    }
  }, [selectedWorkflowId, searchParams, navigate]);

  // State for bulk create modal
  const [isBulkCreateModalOpen, setIsBulkCreateModalOpen] = useState(false);

  // Use cursor pagination hook with reset dependencies
  const { currentPage, handlePageChange, getCurrentCursor } = useCursorPagination({
    pageType: 'campaigns',
    resetDependencies: [campaignPageSize, selectedWorkflowId],
  });

  // Fetch workflows for selected program (needed for bulk create)
  const { isLoading: workflowsLoading } = useWorkflowsByProgram(selectedProgramId || '');

  // Fetch campaigns for selected workflow using custom hook with cursor-based pagination
  const {
    data: campaignsData,
    isLoading: campaignsLoading,
    refetch: refetchCampaigns,
    error,
  } = useCampaignsByWorkflow(selectedWorkflowId, {
    limit: campaignPageSize,
    start_after_id: getCurrentCursor(),
  });

  const campaigns = campaignsData?.campaigns || [];
  // Infer if there's a next page by checking if we got a full page of results
  const hasNextPage = campaigns.length >= campaignPageSize;

  // Store the last campaign ID for next page navigation
  const lastCampaignId = campaigns.length > 0 ? campaigns[campaigns.length - 1]?.id : undefined;

  // Collect created_by user IDs for user lookup
  const createdByUserIds = useMemo(
    () => campaigns.map(c => c.created_by).filter((id): id is string => !!id),
    [campaigns],
  );
  const { usersMap } = useUsersByIds(createdByUserIds);

  const isLoadingState =
    isLoadingOrgAndPrograms || workflowsLoading || (!!selectedWorkflowId && campaignsLoading);

  // Create table configuration
  const tableConfig = useMemo(() => createCampaignsTableConfig(usersMap), [usersMap]);

  // Render breadcrumb and actions early
  const breadcrumb = (
    <BreadcrumbPortal>
      <Breadcrumb>
        <Breadcrumb.Item>Campaigns</Breadcrumb.Item>
      </Breadcrumb>
    </BreadcrumbPortal>
  );

  const headerActions = (
    <HeaderActionsPortal>
      <Button
        size="sm"
        onClick={() => setIsBulkCreateModalOpen(true)}
        className="flex items-center gap-2"
        disabled={!selectedWorkflowId || !selectedProgramId}
      >
        <Upload className="w-4 h-4" />
        Create Campaign
      </Button>
    </HeaderActionsPortal>
  );

  // Show empty state if no workflow selected AND not loading workflows
  if (!selectedWorkflowId && !isLoadingOrgAndPrograms && !workflowsLoading) {
    return (
      <>
        {breadcrumb}
        {headerActions}
        <div className="flex-1 flex items-center justify-center h-full">
          <p className="text-muted-foreground">Please select a workflow to view campaigns</p>
        </div>
      </>
    );
  }

  if (error) {
    const buttonConfig = getErrorButtonConfig(error?.APIHttpStatus, navigate);
    return (
      <>
        {breadcrumb}
        {headerActions}
        <ErrorContainer
          errorMessage={error?.userMessage}
          buttonText={buttonConfig?.text}
          buttonOnClick={buttonConfig?.onClick}
        />
      </>
    );
  }

  return (
    <>
      {breadcrumb}
      {headerActions}

      <div className="flex flex-col h-full">
        {/* Main Content */}
        <div className="flex-1 px-6 overflow-hidden">
          <div className="flex flex-col h-full min-h-0">
            <GenericTable
              config={tableConfig}
              data={campaigns}
              loading={isLoadingState}
              error={error ? String(error) : null}
              currentPage={currentPage}
              hasNextPage={hasNextPage}
              onPageChange={newPage => handlePageChange(newPage, lastCampaignId)}
              pageSize={campaignPageSize}
              pageSizeOptions={[...PAGE_SIZE_OPTIONS]}
              onPageSizeChange={setCampaignPageSize}
            />
          </div>
        </div>

        {/* Bulk Create Cases Modal */}
        <BulkCreateCasesModal
          open={isBulkCreateModalOpen}
          onOpenChange={setIsBulkCreateModalOpen}
          onSuccess={() => {
            refetchCampaigns();
            setIsBulkCreateModalOpen(false);
          }}
        />
      </div>
    </>
  );
};

export { CampaignsContainer };
